// dropdown สลับแบรนด์บนหัวแถบซ้าย — รายชื่อคือแบรนด์ที่ enable จาก Setting เท่านั้น
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { Brand } from "@contracts/api";
import { BrandLogo } from "@features/brands/components/brand-logo";
import { IconChevronDown, IconSearch } from "@shared/ui/icons";
import { menuPath } from "../model/side-menus";
import { useSidebarStore } from "../model/sidebar-store";
import { cn } from "@shared/lib/utils";

export function BrandSwitcher({ brand, brands }: { brand: Brand; brands: Brand[] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  /** ปิดแล้วล้างคำค้นเสมอ — เปิดใหม่ควรได้รายชื่อเต็ม ไม่ใช่ผลค้นของเมื่อกี้ */
  const close = () => {
    setOpen(false);
    setQ("");
  };
  const collapsed = useSidebarStore((s) => s.collapsed);
  const expand = useSidebarStore((s) => s.expand);

  // คลิกที่อื่นในหน้า = ปิด dropdown (เหมือนของเดิมที่ผูกไว้กับ document)
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) close();
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  // เปิดมาพร้อมพิมพ์ได้เลย — โฟกัสไม่ใช่การเปลี่ยน state จึงอยู่ใน effect ได้
  useEffect(() => {
    if (open) search.current?.focus();
  }, [open]);

  /**
   * เรียงตามตัวอักษร ไม่ใช่ตามลำดับที่ server ส่งมา
   *
   * ลำดับเดิมคือลำดับที่ enable ไว้ ซึ่งไม่มีความหมายกับคนหาแบรนด์ — ยี่สิบกว่าชื่อ
   * เรียงแบบไม่มีกฎทำให้ต้องกวาดตาทั้งรายการทุกครั้ง · ใช้ localeCompare แบบไทย
   * เพราะมีทั้งชื่อไทยและอังกฤษปนกัน
   */
  const sorted = [...brands].sort((a, b) => a.name.localeCompare(b.name, "th"));
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? sorted.filter((b) => `${b.name} ${b.tagline}`.toLowerCase().includes(needle))
    : sorted;

  return (
    <div className="brand-select" ref={wrap}>
      <button
        type="button"
        className="brand-select-btn"
        onClick={() => {
          // แถบหุบอยู่ dropdown จะโดนขอบ sidebar ตัด — กางแถบออกก่อน
          if (collapsed) {
            expand();
            return;
          }
          if (open) close();
          else setOpen(true);
        }}
      >
        <BrandLogo brand={brand} className="mini-logo" />
        <span className="name">{brand.name}</span>
        <IconChevronDown />
      </button>
      <div className={cn("brand-select-menu", open && "open")}>
        {/* mousedown ไม่ปิด dropdown เพราะ input อยู่ในกล่องเดียวกับ onDocClick อยู่แล้ว */}
        <div className="brand-select-search">
          <IconSearch size={15} />
          <input
            ref={search}
            type="text"
            placeholder="ค้นหาแบรนด์…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {!shown.length && <p className="brand-select-none muted">ไม่พบแบรนด์ที่ค้น</p>}
        {shown.map((b) => (
          <Link
            key={b.id}
            to={menuPath(b.id, "overview")}
            className={b.id === brand.id ? "current" : ""}
            onClick={close}
          >
            <BrandLogo brand={b} className="mini-logo" />
            <span>{b.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
